import type { SafetyReportArtifactDetailResponse, SafetyScanReportV2Payload } from '../../services/reportsApi';

export type ExternalSafetyReviewModel = {
  reportId: string;
  title: string;
  statusLabel: string;
  scanWindow: { from?: string | null; to?: string | null };
  isDemoOrLoopedSource: boolean;
  controlledDemoBannerText?: string | null;
  responsibleUseBoundaryText?: string | null;
  linkedSafetyCase: {
      safetyCaseId?: string | null;
      label?: string | null;
      statusLabel?: string | null;
    };
  occurrenceSummary: {
    observed_signals?: unknown;
    peak_period?: unknown;
    summary?: string | null;
  };
  reviewActionState: {
    corrective_action_label?: string | null;
    follow_up_label?: string | null;
    follow_up_summary?: string | null;
    outcome_status_label?: string | null;
    summary?: string | null;
  };
  workplaceConditionObserved: {
    conditionLabel: string;
    areaLabel: string;
    workplaceCondition?: Record<string, unknown> | null;
  };
  evidenceSummary: Record<string, unknown>;
  correctiveActionStatus: Record<string, unknown>;
  followUpReadiness: { label?: string | null; summary?: string | null };
  claimBoundaries: Record<string, unknown>;
  buyerReport?: Record<string, unknown> | null;
  closureSummary?: Record<string, unknown> | null;
  representativeEvidence?: unknown;
  decisionReadiness?: Record<string, unknown> | null;
  safetyScanReportV2?: SafetyScanReportV2Payload | null;
  packageDownloadPath?: string | null;
};

/** Map backend external_review payload or artifact response to the page model. */
export function toExternalSafetyReviewModel(
  input: Record<string, unknown> | SafetyReportArtifactDetailResponse,
): ExternalSafetyReviewModel | null {
  const external = (input as Record<string, unknown>).external_review;
  const source =
    external && typeof external === 'object'
      ? (external as Record<string, unknown>)
      : (input as Record<string, unknown>);

  const reportId = String(source.report_id || '').trim();
  if (!reportId) return null;

  const workplace = (source.workplace_condition_observed as Record<string, unknown>) || {};
  const scanWindow = (source.scan_window as Record<string, unknown>) || {};

  return {
    reportId,
    title: String(source.title || 'Edgentik Signed Safety Scan'),
    statusLabel: String(source.status_label || 'Signed'),
    scanWindow: {
      from: (scanWindow.from as string) || null,
      to: (scanWindow.to as string) || null,
    },
    isDemoOrLoopedSource: Boolean(source.is_demo_or_looped_source),
    controlledDemoBannerText: (source.controlled_demo_banner_text as string) || null,
    responsibleUseBoundaryText: (source.responsible_use_boundary_text as string) || null,
    linkedSafetyCase: {
      safetyCaseId: String((source.linked_safety_case as Record<string, unknown>)?.safety_case_id || '').trim() || null,
      label: String((source.linked_safety_case as Record<string, unknown>)?.label || '').trim() || null,
      statusLabel: String((source.linked_safety_case as Record<string, unknown>)?.status_label || '').trim() || null,
    },
    occurrenceSummary: {
      observed_signals: (source.occurrence_summary as Record<string, unknown>)?.observed_signals,
      peak_period: (source.occurrence_summary as Record<string, unknown>)?.peak_period,
      summary: String((source.occurrence_summary as Record<string, unknown>)?.summary || '').trim() || null,
    },
    reviewActionState: {
      corrective_action_label: String(
        (source.review_action_state as Record<string, unknown>)?.corrective_action_label || '',
      ).trim() || null,
      follow_up_label: String((source.review_action_state as Record<string, unknown>)?.follow_up_label || '').trim() || null,
      follow_up_summary: String((source.review_action_state as Record<string, unknown>)?.follow_up_summary || '').trim() || null,
      outcome_status_label: String(
        (source.review_action_state as Record<string, unknown>)?.outcome_status_label || '',
      ).trim() || null,
      summary: String((source.review_action_state as Record<string, unknown>)?.summary || '').trim() || null,
    },
    workplaceConditionObserved: {
      conditionLabel: String(workplace.condition_label || 'Workplace condition observed'),
      areaLabel: String(workplace.area_label || 'Site area'),
      workplaceCondition: (workplace.workplace_condition as Record<string, unknown>) || null,
    },
    evidenceSummary: (source.evidence_summary as Record<string, unknown>) || {},
    correctiveActionStatus: (source.corrective_action_status as Record<string, unknown>) || {},
    followUpReadiness: {
      label: (source.follow_up_readiness as Record<string, unknown>)?.label as string | null,
      summary: (source.follow_up_readiness as Record<string, unknown>)?.summary as string | null,
    },
    claimBoundaries: (source.claim_boundaries as Record<string, unknown>) || {},
    buyerReport: (source.buyer_report as Record<string, unknown>) || null,
    closureSummary: (source.closure_summary as Record<string, unknown>) || null,
    representativeEvidence: source.representative_evidence,
    decisionReadiness: (source.decision_readiness as Record<string, unknown>) || null,
    safetyScanReportV2: (source.safety_scan_report_v2 as SafetyScanReportV2Payload) || null,
    packageDownloadPath: (source.package_download_path as string) || null,
  };
}
